import apiClient from '@/api';
import {
    formatSingleSeries,
    formatSingleSeriesDescending,
    formatTimeSeriesMonthlyRecent,
    formatTimeSeriesHourlyRecent2h,
    formatDistributionPie,
    formatResultDistributionPie,
    formatVersionDistribution,
    formatVersionWindowDistribution,
    formatTestResultTimeline,
    formatBatteryTypeMonthComparison,
    formatTestTypeQuarterlyPie,
} from './advanced-dashboard/chartDataFormatters';
import { getZonedDateTimeParts, resolveTimeZone } from '@/utils/timezone';

// Previous quarter label based on provided time zone (IANA), e.g. 2025Q3
const getPreviousQuarterLabel = timeZone => {
    const tz = resolveTimeZone(timeZone);
    const { year, month } = getZonedDateTimeParts(Date.now(), tz);
    const monthIndex = Math.max(0, Number(month) - 1); // 0-11
    const currentQuarter = Math.floor(monthIndex / 3) + 1; // 1-4

    let quarter = currentQuarter - 1;
    let qYear = year;

    if (quarter === 0) {
        quarter = 4;
        qYear -= 1;
    }

    return `${qYear}Q${quarter}`;
};

// Mock builder: generate metric values & trends based on static defs
// visibleOn: 'total-only' | 'all-models' | 'every-model-only'
const userShopMetricDefs = [
    {
        key: 'new_user_daily',
        type: 'success',
        icon: 'fa fa-user-plus',
        title: 'New Users',
        visibleOn: 'total-only',
    },
    {
        key: 'active_user_daily',
        type: 'success',
        icon: 'fa fa-user',
        title: 'Active Users',
        visibleOn: 'all-models',
    },
    {
        key: 'total_user',
        type: 'success',
        icon: 'fa fa-users',
        title: 'Total Users',
        visibleOn: 'total-only',
    },
    {
        key: 'new_shop_daily',
        type: 'info',
        icon: 'fa fa-plus-square',
        title: 'New Shops',
        visibleOn: 'total-only',
    },
    {
        key: 'active_shop_daily',
        type: 'info',
        icon: 'fa fa-cog',
        title: 'Active Shops',
        visibleOn: 'all-models',
    },
    {
        key: 'total_shop',
        type: 'info',
        icon: 'fa fa-cogs',
        title: 'Total Shops',
        visibleOn: 'total-only',
    },
];

const batteryMetricDefsBase = [
    {
        key: 'total_battery_test',
        type: 'primary',
        icon: 'fa fa-bar-chart',
        titleTemplate: label => `${label} Battery Tests`,
        visibleOn: 'all-models',
    },
    {
        key: 'active_club_quarterly',
        type: 'primary',
        icon: 'fa fa-briefcase',
        titleTemplate: label => `${label} Active Businesses`,
        visibleOn: 'total-only',
    },
    {
        key: 'active_user_quarterly',
        type: 'primary',
        icon: 'fa fa-user-o',
        titleTemplate: label => `${label} Active Users`,
        visibleOn: 'total-only',
    },
    {
        key: 'active_device_quarterly',
        type: 'primary',
        icon: 'fa fa-tablet',
        titleTemplate: label => `${label} Unique SNs`,
        visibleOn: 'every-model-only',
    },
    {
        key: 'battery_test_quarterly',
        type: 'primary',
        icon: 'fa fa-battery-half',
        title: 'Total Tests',
        visibleOn: 'total-only',
    },
];

const buildBatteryMetricDefs = timeZone => {
    const previousQuarterLabel = getPreviousQuarterLabel(timeZone);
    return batteryMetricDefsBase.map(def => ({
        ...def,
        title:
            typeof def.titleTemplate === 'function'
                ? def.titleTemplate(previousQuarterLabel)
                : def.title,
    }));
};
const METRIC_KEYS = [
    ...userShopMetricDefs.map(def => def.key),
    ...batteryMetricDefsBase.map(def => def.key),
];
const METRIC_KEY_SET = new Set(METRIC_KEYS);

// --- Optional per-key overrides ---
// Define custom behavior for metrics and charts by key.
// Fill these objects with entries to override defaults without touching core logic.
const METRIC_OVERRIDES = {
    // Example:
    // active_club_quarterly: {
    //     valueSelector: (data, row) => Number(data?.total_records ?? 0),
    //     trendSelector: (data, row) => String(data?.change ?? '0'),
    //     titleResolver: (row, def) => row?.config?.title || def.title,
    //     infoResolver: (row, def) => row?.config?.info || def.info,
    //     type: 'primary',
    //     icon: 'ti-briefcase',
    // },
};

// Data formatters imported from chartDataFormatters.js
// No local definitions needed - all formatters are centralized

const stackedVersionOptions = () => ({
    scales: {
        x: { stacked: true },
        y: { stacked: true },
    },
    plugins: {
        tooltip: { mode: 'index', intersect: false },
    },
});

const CHART_OVERRIDES = {
    // Time-series volume
    battery_test_past_12_monthly: {
        chartType: 'line',
        dataBuilder: row => formatTimeSeriesMonthlyRecent(row, 12),
        optionsBuilder: row => {
            const data = row && row.data ? row.data : {};
            const maxVal = Math.max(
                ...Object.values(data).map(v => Number(v) || 0)
            );
            return {
                scales: {
                    y: {
                        beginAtZero: true,
                        suggestedMax: Math.ceil(maxVal * 1.1),
                    },
                },
            };
        },
        width: 12,
    },
    battery_test_past_monthly: {
        chartType: 'line',
        dataBuilder: row => formatTimeSeriesMonthlyRecent(row, 36),
        optionsBuilder: row => {
            const data = row && row.data ? row.data : {};
            const maxVal = Math.max(
                ...Object.values(data).map(v => Number(v) || 0)
            );
            return {
                scales: {
                    y: {
                        beginAtZero: true,
                        suggestedMax: Math.ceil(maxVal * 1.1),
                    },
                },
            };
        },
        width: 6,
    },
    battery_test_upload_hourly: {
        chartType: 'bar',
        dataBuilder: (row, opts = {}) =>
            formatTimeSeriesHourlyRecent2h(row, opts.timeZone),
        width: 6,
    },

    // Result distributions
    battery_test_result_past_year: {
        chartType: 'pie',
        dataBuilder: formatResultDistributionPie,
        optionsBuilder: row =>
            buildResultPieOptions(row, {
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom',
                    },
                },
            }),
        visibleOn: 'every-model-only',
        width: 6,
    },
    total_battery_test_result: {
        chartType: 'pie',
        dataBuilder: formatResultDistributionPie,
        optionsBuilder: row =>
            buildResultPieOptions(row, {
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom',
                    },
                },
            }),
        visibleOn: 'every-model-only',
        width: 6,
    },
    battery_test_result_with_sd_past_year: {
        chartType: 'pie',
        dataBuilder: formatResultDistributionPie,
        optionsBuilder: row => buildResultPieOptions(row),
        width: 4,
    },
    battery_test_result_without_sd_past_year: {
        chartType: 'pie',
        dataBuilder: formatResultDistributionPie,
        optionsBuilder: row => buildResultPieOptions(row),
        width: 4,
    },
    battery_test_result_past_monthly: {
        chartType: 'line',
        dataBuilder: row => formatTimeSeriesMonthlyRecent(row, 12),
        optionsBuilder: row => {
            const data = row && row.data ? row.data : {};
            const maxVal = Math.max(
                ...Object.values(data).map(v => Number(v) || 0)
            );

            return {
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom',
                    },
                },
                layout: {
                    padding: {
                        top: 30,
                    },
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        suggestedMax: Math.ceil(maxVal * 1.1),
                    },
                },
            };
        },
        visibleOn: 'every-model-only',
        width: 6,
    },
    battery_test_result_current_month: {
        chartType: 'pie',
        dataBuilder: formatResultDistributionPie,
        optionsBuilder: row =>
            buildResultPieOptions(row, {
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom',
                    },
                },
            }),
        visibleOn: 'every-model-only',
        width: 6,
    },
    battery_test_battery_type_month_comparison: {
        chartType: 'bar',
        dataBuilder: formatBatteryTypeMonthComparison,
        optionsBuilder: () => ({
            plugins: {
                legend: {
                    display: true,
                    position: 'bottom',
                },
            },
            scales: {
                x: {
                    stacked: false,
                    ticks: {
                        autoSkip: false,
                    },
                },
                y: {
                    stacked: false,
                    beginAtZero: true,
                },
            },
            datasets: {
                bar: {
                    barPercentage: 0.7,
                    categoryPercentage: 0.6,
                },
            },
        }),
        visibleOn: 'every-model-only',
        width: 4,
    },
    battery_test_test_type_quarterly: {
        chartType: 'pie',
        dataBuilder: formatTestTypeQuarterlyPie,
        optionsBuilder: () => ({
            plugins: {
                legend: {
                    display: true,
                    position: 'bottom',
                },
            },
        }),
        titleResolver: (row, chart) =>
            extractCardTitle(row, chart) || chart.title || '',
        infoResolver: row => {
            const calcDateStr = row?.config?.calculate_date;
            const ref = calcDateStr ? new Date(calcDateStr) : new Date();
            const q = Math.floor(ref.getMonth() / 3) + 1;
            const year = ref.getFullYear();
            return `${year} Q${q}`;
        },
        showSubTitle: true,
        visibleOn: 'every-model-only',
        width: 4,
    },

    // Top-N active users/shops
    top_five_active_user_daily: {
        chartType: 'bar',
        dataBuilder: formatSingleSeriesDescending,
        width: 6,
    },
    top_five_active_user_past_week: {
        chartType: 'bar',
        dataBuilder: formatSingleSeriesDescending,
        width: 6,
    },
    top_five_active_user_past_monthly: {
        chartType: 'bar',
        dataBuilder: formatSingleSeriesDescending,
        width: 6,
    },
    top_five_active_shop_daily: {
        chartType: 'bar',
        dataBuilder: formatSingleSeriesDescending,
        width: 6,
    },
    top_five_active_shop_past_week: {
        chartType: 'bar',
        dataBuilder: formatSingleSeriesDescending,
        width: 6,
    },
    top_five_active_shop_past_monthly: {
        chartType: 'bar',
        dataBuilder: formatSingleSeriesDescending,
        width: 6,
    },

    // User growth
    new_create_user_monthly: {
        chartType: 'line',
        dataBuilder: row => formatTimeSeriesMonthlyRecent(row),
        visibleOn: 'total-only',
        optionsBuilder: row => {
            const data = row && row.data ? row.data : {};
            const maxVal = Math.max(
                ...Object.values(data).map(v => Number(v) || 0)
            );
            return {
                scales: {
                    y: {
                        beginAtZero: true,
                        suggestedMax: Math.ceil(maxVal * 1.1),
                    },
                },
            };
        },
        width: 6,
    },
    total_user_monthly: {
        chartType: 'line',
        dataBuilder: row => formatTimeSeriesMonthlyRecent(row),
        visibleOn: 'total-only',
        optionsBuilder: row => {
            const data = row && row.data ? row.data : {};
            const maxVal = Math.max(
                ...Object.values(data).map(v => Number(v) || 0)
            );
            return {
                scales: {
                    y: {
                        beginAtZero: true,
                        suggestedMax: Math.ceil(maxVal * 1.1),
                    },
                },
            };
        },
        width: 6,
    },

    // Firmware / App versions
    total_fw_version: {
        chartType: 'bar',
        dataBuilder: formatVersionDistribution,
        visibleOn: 'every-model-only',
        width: 6,
    },
    // FW versions: daily / week use flat distribution; monthly uses windowed/map
    fw_version_daily: {
        chartType: 'bar',
        dataBuilder: formatVersionDistribution,
        visibleOn: 'every-model-only',
        width: 6,
    },
    fw_version_past_week: {
        chartType: 'bar',
        dataBuilder: formatVersionDistribution,
        visibleOn: 'every-model-only',
        width: 6,
    },
    fw_version_past_monthly: {
        chartType: 'bar',
        dataBuilder: row => formatVersionWindowDistribution(row, '30d'), // last year to now
        optionsBuilder: stackedVersionOptions,
        visibleOn: 'every-model-only',
        width: 6,
    },
    total_app_version: {
        chartType: 'bar',
        dataBuilder: formatVersionDistribution,
        visibleOn: 'BTW350-only',
        width: 6,
    },
    // App versions: daily / week use flat distribution; monthly uses windowed/map
    app_version_daily: {
        chartType: 'bar',
        dataBuilder: formatVersionDistribution,
        visibleOn: 'BTW350-only',
        width: 6,
    },
    app_version_past_week: {
        chartType: 'bar',
        dataBuilder: formatSingleSeries,
        visibleOn: 'BTW350-only',
        width: 6,
    },
    app_version_past_monthly: {
        chartType: 'bar',
        dataBuilder: row => formatVersionWindowDistribution(row, 'year'), // last year to now
        optionsBuilder: stackedVersionOptions,
        visibleOn: 'BTW350-only',
        width: 6,
    },
    battery_test_battery_type_current_year: {
        chartType: 'pie',
        dataBuilder: formatDistributionPie,
        optionsBuilder: () => ({
            plugins: {
                legend: {
                    display: true,
                    position: 'bottom',
                },
            },
        }),
        visibleOn: 'every-model-only',
        width: 4,
    },
};

// Unified handling for top-five-active-shop charts (day/week/month...)
const TOP_FIVE_ACTIVE_SHOP_CODES = [
    'top_five_active_shop_daily',
    'top_five_active_shop_past_week',
    'top_five_active_shop_past_monthly',
];

const TOP_FIVE_ACTIVE_SHOP_COMBINED_KEY = 'top_five_active_shop_combined';

const getTopFiveActiveShopLabel = code => {
    switch (code) {
        case 'top_five_active_shop_daily':
            return 'chart.chart_selector__period_yesterday';
        case 'top_five_active_shop_past_week':
            return 'chart.chart_selector__period_last_7_days';
        case 'top_five_active_shop_past_monthly':
            return 'chart.chart_selector__period_this_month';
        default:
            return code;
    }
};

const TOP_FIVE_ACTIVE_USER_CODES = [
    'top_five_active_user_daily',
    'top_five_active_user_past_week',
    'top_five_active_user_past_monthly',
];

const TOP_FIVE_ACTIVE_USER_COMBINED_KEY = 'top_five_active_user_combined';

const getTopFiveActiveUserLabel = code => {
    switch (code) {
        case 'top_five_active_user_daily':
            return 'chart.chart_selector__period_yesterday';
        case 'top_five_active_user_past_week':
            return 'chart.chart_selector__period_last_7_days';
        case 'top_five_active_user_past_monthly':
            return 'chart.chart_selector__period_this_month';
        default:
            return code;
    }
};

// Test result monthly combined
const TEST_RESULT_MONTHLY_CODE = 'battery_test_result_past_monthly';
const TEST_RESULT_MONTHLY_COMBINED_KEY =
    'battery_test_result_past_monthly_combined';
const TEST_RESULT_MODES = [
    {
        code: 'monthly',
        label: 'chart.chart_selector__period_monthly',
        ranges: [6, 12, 18, 24],
    },
    {
        code: 'quarterly',
        label: 'chart.chart_selector__period_quarterly',
        ranges: [4, 6, 8],
    },
    {
        code: 'yearly',
        label: 'chart.chart_selector__period_yearly',
        ranges: [],
    }, // all years
];

// Firmware version charts (with period variants)
const FW_VERSION_CODES = [
    'total_fw_version',
    'fw_version_daily',
    'fw_version_past_week',
    'fw_version_past_monthly',
];
const FW_VERSION_COMBINED_KEY = 'fw_version_combined';
const getFwVersionLabel = code => {
    switch (code) {
        case 'total_fw_version':
            return 'chart.chart_selector__period_total';
        case 'fw_version_daily':
            return 'chart.chart_selector__period_day';
        case 'fw_version_past_week':
            return 'chart.chart_selector__period_week';
        case 'fw_version_past_monthly':
            return 'chart.chart_selector__period_month';
        default:
            return code;
    }
};

// App version charts (with period variants)
const APP_VERSION_CODES = [
    'total_app_version',
    'app_version_daily',
    'app_version_past_week',
    'app_version_past_monthly',
];
const APP_VERSION_COMBINED_KEY = 'app_version_combined';
const getAppVersionLabel = code => {
    switch (code) {
        case 'total_app_version':
            return 'chart.chart_selector__period_total';
        case 'app_version_daily':
            return 'chart.chart_selector__period_day';
        case 'app_version_past_week':
            return 'chart.chart_selector__period_week';
        case 'app_version_past_monthly':
            return 'chart.chart_selector__period_month';
        default:
            return code;
    }
};

// Combined variant grouping (top-5, fw/app versions)
const COMBINED_GROUP_DEFS = [
    {
        key: TOP_FIVE_ACTIVE_SHOP_COMBINED_KEY,
        codes: TOP_FIVE_ACTIVE_SHOP_CODES,
        labelResolver: getTopFiveActiveShopLabel,
        defaultWidth: 6,
        handler: 'topFive',
    },
    {
        key: TOP_FIVE_ACTIVE_USER_COMBINED_KEY,
        codes: TOP_FIVE_ACTIVE_USER_CODES,
        labelResolver: getTopFiveActiveUserLabel,
        defaultWidth: 6,
        handler: 'topFive',
    },
    {
        key: FW_VERSION_COMBINED_KEY,
        codes: FW_VERSION_CODES,
        labelResolver: getFwVersionLabel,
        defaultWidth: 6,
        handler: 'variant',
    },
    {
        key: APP_VERSION_COMBINED_KEY,
        codes: APP_VERSION_CODES,
        labelResolver: getAppVersionLabel,
        defaultWidth: 6,
        handler: 'variant',
    },
];
const COMBINED_GROUP_DEF_MAP = COMBINED_GROUP_DEFS.reduce(
    (map, def) => map.set(def.key, def),
    new Map()
);

// --- Utilities for extracting values from API response ---
const extractMetricValue = data => {
    if (data === null || data === undefined) return 0;
    if (typeof data === 'number') return Number.isFinite(data) ? data : 0;
    if (typeof data === 'string') {
        const num = Number(data);
        return Number.isFinite(num) ? num : 0;
    }
    if (Array.isArray(data)) {
        return data.reduce(
            (sum, v) => sum + (Number.isFinite(Number(v)) ? Number(v) : 0),
            0
        );
    }
    if (typeof data === 'object') {
        // Prefer total_records if present
        if (
            'total_records' in data &&
            Number.isFinite(Number(data.total_records))
        ) {
            return Number(data.total_records);
        }
        // Fallback: sum all numeric values
        return Object.values(data).reduce((sum, v) => {
            const num = extractMetricValue(v);
            return sum + (Number.isFinite(num) ? num : 0);
        }, 0);
    }
    return 0;
};

const extractTrendValue = data => {
    if (data && typeof data === 'object' && 'change' in data) {
        return String(data.change);
    }
    return '0';
};

const extractTrendDirection = data => {
    if (!data || typeof data !== 'object' || !('change' in data)) {
        return 'flat';
    }
    const changeNum = Number(data.change);
    if (changeNum > 0) return 'up';
    if (changeNum < 0) return 'down';
    return 'flat'; // changeNum === 0
};

const extractCardTitle = (row, def) => {
    return (row && row.config && row.config.title) || (def && def.title) || '';
};

const extractCardInfo = (row, def) => {
    return (row && row.config && row.config.info) || (def && def.info) || '';
};

// Result pie helpers: center text (total + replace ratio)
const RESULT_REPLACE_LABELS = new Set([
    'BAD & REPLACE',
    'BAD CELL REPLACE',
    'CAUTION',
]);

const summarizeResultDistribution = row => {
    const data = (row && row.data) || {};
    let total = 0;
    let replace = 0;

    Object.entries(data).forEach(([label, value]) => {
        const count = Number(value) || 0;
        total += count;
        const normalized = String(label || '')
            .trim()
            .toUpperCase();
        if (RESULT_REPLACE_LABELS.has(normalized)) {
            replace += count;
        }
    });

    const replacePct = total > 0 ? (replace / total) * 100 : 0;
    return {
        total,
        replace,
        replacePct,
    };
};

const buildResultPieOptions = (row, baseOptions = {}) => {
    const summary = summarizeResultDistribution(row);
    const textLines = [
        `Total: ${summary.total.toLocaleString()}`,
        `Replace: ${summary.replacePct.toFixed(1)}%`,
    ];

    return {
        ...baseOptions,
        plugins: {
            ...(baseOptions.plugins || {}),
            doughnutCenterText: {
                ...(baseOptions.plugins &&
                baseOptions.plugins.doughnutCenterText
                    ? baseOptions.plugins.doughnutCenterText
                    : {}),
                text: () => textLines,
                fontSize: 15,
                lineSpacing: 2,
            },
        },
    };
};

// Layout helpers
const clampWidth = value => {
    if (!Number.isFinite(value)) return null;
    return Math.min(12, Math.max(1, Math.round(value)));
};

const getLayoutWidth = item => {
    if (!item) return null;
    const explicit =
        Number(item.chart_width) ||
        Number(item.width) ||
        Number(item.block_width);
    return clampWidth(explicit);
};

const getLayoutWeight = item => {
    if (!item) return 0;
    const weight =
        Number(item.chart_weight) ||
        Number(item.weight) ||
        Number(item.block_weight);
    return Number.isFinite(weight) && weight > 0 ? weight : 0;
};

const resolveChartWidth = (
    item,
    override = {},
    defaultWidth = 6,
    totalWeight = 0
) => {
    const explicit = getLayoutWidth(item);
    if (explicit) return explicit;

    const itemWeight = getLayoutWeight(item);
    if (itemWeight > 0 && totalWeight > 0) {
        const weighted = (itemWeight / totalWeight) * 12;
        const clampedWeighted = clampWidth(weighted);
        if (clampedWeighted) return clampedWeighted;
    }

    const overrideWidth = clampWidth(Number(override.width));
    if (overrideWidth) return overrideWidth;

    return clampWidth(defaultWidth) || 6;
};

// Extract the newest calculate_date across analytics rows
const parseDateSafe = value => {
    if (!value) return null;
    const parsed =
        value instanceof Date
            ? value
            : typeof value === 'number' || typeof value === 'string'
            ? new Date(value)
            : null;
    if (!parsed || Number.isNaN(parsed.getTime())) return null;
    return parsed;
};

const findLatestCalculateDate = analyticsMap => {
    let latest = null;
    if (!analyticsMap || typeof analyticsMap !== 'object') {
        return null;
    }

    Object.values(analyticsMap).forEach(row => {
        const calcDate = parseDateSafe(row?.config?.calculate_date);
        if (calcDate && (!latest || calcDate > latest)) {
            latest = calcDate;
        }
    });

    return latest ? latest.toISOString() : null;
};

// Helper: check if chart should be visible for given device
const isChartVisibleForDevice = (chartOverride, device) => {
    const visibleOn =
        (chartOverride && chartOverride.visibleOn) || 'all-models';
    const isTotalDevice = device === 'Total';

    switch (visibleOn) {
        case 'total-only':
            return isTotalDevice;
        case 'every-model-only':
            return !isTotalDevice; // Only models, not Total
        case 'all-models':
        default:
            return true;
    }
};

// Helper: check if metric should be visible for given device
const isMetricVisibleForDevice = (metricDef, device) => {
    if (!metricDef) return true; // No restriction
    const visibleOn = metricDef.visibleOn || 'all-models';
    const isTotalDevice = device === 'Total';

    switch (visibleOn) {
        case 'total-only':
            return isTotalDevice;
        case 'every-model-only':
            return !isTotalDevice; // Only models, not Total
        case 'all-models':
        default:
            return true; // Show on all
    }
};

const buildMetrics = (groupLayout, analyticsMap, device, timeZone) => {
    const map = {};
    if (!Array.isArray(groupLayout)) return map;

    const metricDefs = [
        ...userShopMetricDefs,
        ...buildBatteryMetricDefs(timeZone),
    ];

    const findDefForKey = key => metricDefs.find(def => def.key === key);

    groupLayout.forEach(item => {
        if (!item || !item.chart_code) return;
        const key = item.chart_code;
        if (!METRIC_KEY_SET.has(key)) return;
        if (map[key]) return;
        const def = findDefForKey(key);
        if (!def) return;

        // Check visibility based on device
        if (!isMetricVisibleForDevice(def, device)) {
            return; // Skip this metric for current device
        }

        const metricOverride = METRIC_OVERRIDES[key] || {};

        const itemDevice = item.chart_device || 'Total';
        const analyticsKey = `${key}__${itemDevice}`;
        const row = analyticsMap && analyticsMap[analyticsKey];
        const rawData = row && row.data ? row.data : null;
        const valueNum =
            typeof metricOverride.valueSelector === 'function'
                ? metricOverride.valueSelector(rawData, row)
                : extractMetricValue(rawData);
        const safeValue = Number.isFinite(valueNum) ? valueNum : 0;

        map[key] = {
            title:
                typeof metricOverride.titleResolver === 'function'
                    ? metricOverride.titleResolver(row, def)
                    : extractCardTitle(row, def),
            icon: metricOverride.icon || def.icon,
            type: metricOverride.type || def.type,
            info:
                typeof metricOverride.infoResolver === 'function'
                    ? metricOverride.infoResolver(row, def)
                    : extractCardInfo(row, def),
            value: safeValue.toLocaleString(),
            trend: {
                direction: extractTrendDirection(rawData),
                value:
                    typeof metricOverride.trendSelector === 'function'
                        ? metricOverride.trendSelector(rawData, row)
                        : extractTrendValue(rawData),
            },
        };
    });
    return map;
};
const buildSections = (context, groupLayout) => {
    const commonRoles = ['superadmin', 'business', 'shop', 'basic'];

    if (!Array.isArray(groupLayout) || groupLayout.length === 0) {
        return [];
    }

    const blocksMap = new Map();

    groupLayout.forEach(item => {
        if (!item || item.block === undefined || item.block === null) {
            return;
        }
        if (!item.chart_code) {
            return;
        }

        const blockId = Number(item.block);
        if (Number.isNaN(blockId)) {
            return;
        }

        if (!blocksMap.has(blockId)) {
            blocksMap.set(blockId, []);
        }
        blocksMap.get(blockId).push(item);
    });

    const sortedBlocks = Array.from(blocksMap.keys()).sort((a, b) => a - b);

    const sections = [];

    sortedBlocks.forEach(blockId => {
        const rawItems = blocksMap.get(blockId) || [];
        const items = rawItems
            .slice()
            .sort((a, b) => (a.block_order || 0) - (b.block_order || 0));

        if (!items.length) {
            return;
        }

        const hasMetric = items.some(it => METRIC_KEY_SET.has(it.chart_code));
        const hasNonMetric = items.some(
            it => !METRIC_KEY_SET.has(it.chart_code)
        );

        // 1) metrics-only block -> cards-row
        if (hasMetric && !hasNonMetric) {
            const sectionItems = items.map(it => ({
                id: `metric-block${blockId}-${it.chart_code}-${
                    it.chart_device || 'all'
                }`,
                metricKey: it.chart_code,
                device: it.chart_device || null,
            }));

            sections.push({
                id: `block-${blockId}-metrics`,
                type: 'cards-row',
                allowedRoles: commonRoles,
                items: sectionItems,
            });
            return;
        }

        // 2) 其他情況（包含純 charts 或混合）一律先建立 charts-row
        const chartSourceItems = items.filter(
            it => !METRIC_KEY_SET.has(it.chart_code)
        );

        const chartWeightSum = chartSourceItems.reduce(
            (sum, it) => sum + getLayoutWeight(it),
            0
        );

        const chartItems = [];

        const combinedCodeSet = new Set();
        COMBINED_GROUP_DEFS.forEach(def =>
            def.codes.forEach(code => combinedCodeSet.add(code))
        );

        COMBINED_GROUP_DEFS.forEach(def => {
            const matched = chartSourceItems
                .filter(it => def.codes.includes(it.chart_code))
                .sort((a, b) => (a.block_order || 0) - (b.block_order || 0));
            if (!matched.length) {
                return;
            }
            const device = matched[0].chart_device || null;
            const firstOverride = CHART_OVERRIDES[matched[0].chart_code] || {};
            chartItems.push({
                id: `chart-block${blockId}-${def.key}-${device || 'all'}`,
                chartKey: def.key,
                device,
                width: resolveChartWidth(
                    matched[0],
                    firstOverride,
                    def.defaultWidth || 6,
                    chartWeightSum
                ),
                variants: matched.map(it => ({
                    code: it.chart_code,
                    label:
                        typeof def.labelResolver === 'function'
                            ? def.labelResolver(it.chart_code)
                            : it.chart_code,
                })),
            });
        });

        // Collapse battery test result monthly into combined variants
        const testResultMonthlyItems = chartSourceItems.filter(
            it => it.chart_code === TEST_RESULT_MONTHLY_CODE
        );
        if (testResultMonthlyItems.length) {
            const sortedResultItems = testResultMonthlyItems
                .slice()
                .sort((a, b) => (a.block_order || 0) - (b.block_order || 0));
            const device = sortedResultItems[0].chart_device || null;
            chartItems.push({
                id: `chart-block${blockId}-${TEST_RESULT_MONTHLY_COMBINED_KEY}-${
                    device || 'all'
                }`,
                chartKey: TEST_RESULT_MONTHLY_COMBINED_KEY,
                device,
                width: resolveChartWidth(
                    sortedResultItems[0],
                    {},
                    12,
                    chartWeightSum
                ),
                baseCode: TEST_RESULT_MONTHLY_CODE,
                modes: TEST_RESULT_MODES,
            });
        }

        const residualChartItems = chartSourceItems.filter(
            it =>
                !combinedCodeSet.has(it.chart_code) &&
                it.chart_code !== TEST_RESULT_MONTHLY_CODE
        );

        // Other chart items remain one-to-one
        chartItems.push(
            ...residualChartItems.map(it => {
                const override = CHART_OVERRIDES[it.chart_code] || {};
                return {
                    id: `chart-block${blockId}-${it.chart_code}-${
                        it.chart_device || 'all'
                    }`,
                    chartKey: it.chart_code,
                    device: it.chart_device || null,
                    width: resolveChartWidth(it, override, 6, chartWeightSum),
                };
            })
        );

        if (chartItems.length) {
            sections.push({
                id: `block-${blockId}-charts`,
                type: 'charts-row',
                allowedRoles: commonRoles,
                items: chartItems,
            });
        }

        // 3) 若同一個 block 也包含 metrics，另外再建一個 cards-row
        if (hasMetric) {
            const metricItems = items
                .filter(it => METRIC_KEY_SET.has(it.chart_code))
                .map(it => ({
                    id: `metric-block${blockId}-${it.chart_code}-${
                        it.chart_device || 'all'
                    }`,
                    metricKey: it.chart_code,
                    device: it.chart_device || null,
                }));

            if (metricItems.length) {
                sections.push({
                    id: `block-${blockId}-metrics`,
                    type: 'cards-row',
                    allowedRoles: commonRoles,
                    items: metricItems,
                });
            }
        }
    });

    return sections;
};
const buildChartConfigFromAnalyticsRow = (row, options = {}) => {
    if (!row) {
        return null;
    }

    const { timeZone } = options;
    const code = row.code || '';
    const chartOverride = CHART_OVERRIDES[code] || {};
    const description = row.description || '';
    const cfg = row.config || {};
    const rawData = row.data || {};

    const base = {
        id: code,
        title: cfg.title || description || code,
        subTitle: cfg.info || description || '',
        info: cfg.info || '',
        order:
            typeof chartOverride.order === 'number' ? chartOverride.order : 0,
        options: {
            responsive: true,
            maintainAspectRatio: false,
        },
    };

    // Use override dataBuilder if provided
    if (typeof chartOverride.dataBuilder === 'function') {
        const built = chartOverride.dataBuilder(row, { timeZone });
        const optionsExtra =
            typeof chartOverride.optionsBuilder === 'function'
                ? chartOverride.optionsBuilder(row, { timeZone })
                : {};
        return {
            ...base,
            type: chartOverride.chartType || 'line',
            data: built || { labels: [], datasets: [] },
            options: { ...base.options, ...optionsExtra },
        };
    }

    if (rawData && typeof rawData === 'object') {
        const entries = Object.entries(rawData);
        if (entries.length > 0) {
            const firstValue = entries[0][1];

            if (typeof firstValue === 'number') {
                const labels = entries.map(([label]) => label);
                const values = entries.map(([, value]) => Number(value) || 0);

                return {
                    ...base,
                    type: chartOverride.chartType || 'pie',
                    data: {
                        labels,
                        datasets: [
                            {
                                data: values,
                            },
                        ],
                    },
                };
            }

            if (
                firstValue &&
                typeof firstValue === 'object' &&
                !Array.isArray(firstValue)
            ) {
                const sortedEntries = entries.sort(([a], [b]) =>
                    a.localeCompare(b)
                );
                const labels = sortedEntries.map(([label]) => label);
                const totals = sortedEntries.map(([, inner]) => {
                    if (!inner || typeof inner !== 'object') {
                        return 0;
                    }
                    return Object.values(inner).reduce(
                        (sum, v) => sum + (Number(v) || 0),
                        0
                    );
                });

                return {
                    ...base,
                    type: chartOverride.chartType || 'line',
                    data: {
                        labels,
                        datasets: [
                            {
                                label: code,
                                data: totals,
                            },
                        ],
                    },
                };
            }
        }
    }

    return {
        ...base,
        type: chartOverride.chartType || 'line',
        data: {
            labels: [],
            datasets: [],
        },
    };
};

// Helper to aggregate nested month->entity->count data into bar chart by period
const aggregateTopFiveData = (row, period = 'monthly', timeZone, topN = 5) => {
    const data = row?.data || {};
    const monthKeys = Object.keys(data).filter(k => /\d{4}-\d{2}/.test(k));
    if (!monthKeys.length) return { labels: [], datasets: [] };

    const tz = resolveTimeZone(timeZone);
    const nowParts = getZonedDateTimeParts(Date.now(), tz);
    const nowYear = nowParts.year;
    const nowMonthIndex = nowParts.month - 1;
    const nowValue = nowYear * 12 + nowMonthIndex;
    const safeTopN =
        Number.isFinite(Number(topN)) && Number(topN) > 0
            ? Math.floor(Number(topN))
            : null;

    const toMonthValue = (year, monthIndex) => year * 12 + monthIndex;
    const toMonthKey = (year, monthIndex) =>
        `${year}-${String(monthIndex + 1).padStart(2, '0')}`;

    const buildTopDataset = (entries, label = '') => {
        if (!entries.length) return { labels: [], datasets: [] };
        const sorted = entries
            .map(([name, value]) => [name, Number(value) || 0])
            .sort(([, va], [, vb]) => vb - va);
        const limited = safeTopN ? sorted.slice(0, safeTopN) : sorted;
        const labels = limited.map(([name]) => name);
        const values = limited.map(([, value]) => value);
        return {
            labels,
            datasets: [
                {
                    label: label || row?.code || '',
                    data: values,
                },
            ],
        };
    };

    const getLatestMonthKey = () =>
        monthKeys.slice().sort().slice(-1)[0] || null;

    // Special case: period === 'current-month' -> use current or latest month, keep only top N entities
    if (period === 'current-month') {
        const currentKey = toMonthKey(nowYear, nowMonthIndex);
        const targetKey = monthKeys.includes(currentKey)
            ? currentKey
            : getLatestMonthKey();

        if (!targetKey) return { labels: [], datasets: [] };
        const inner = data[targetKey];
        if (!inner || typeof inner !== 'object') {
            return { labels: [], datasets: [] };
        }
        return buildTopDataset(Object.entries(inner), targetKey);
    }

    // Quarterly/yearly: aggregate last 3 or 12 months and rank entities by total
    if (period === 'quarterly' || period === 'yearly') {
        const monthsBack = period === 'quarterly' ? 2 : 11;
        const startValue = nowValue - monthsBack;
        const totals = {};

        monthKeys.forEach(monthKey => {
            const match = /^(\d{4})-(\d{2})$/.exec(monthKey);
            if (!match) return;
            const year = Number(match[1]);
            const monthIndex = Number(match[2]) - 1;
            if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) return;

            const value = toMonthValue(year, monthIndex);
            if (value < startValue || value > nowValue) return;

            const inner = data[monthKey];
            if (!inner || typeof inner !== 'object') return;
            Object.entries(inner).forEach(([entity, count]) => {
                const num = Number(count) || 0;
                totals[entity] = (totals[entity] || 0) + num;
            });
        });

        return buildTopDataset(
            Object.entries(totals),
            period === 'quarterly'
                ? 'chart.chart_selector__period_last_3_months'
                : 'chart.chart_selector__period_last_12_months'
        );
    }

    // Fallback: aggregate totals by month/quarter/year (original behavior)
    const buckets = new Map();
    const rangeLimit =
        period === 'monthly' ? 12 : period === 'quarterly' ? 8 : 3;
    const span =
        period === 'monthly'
            ? 11
            : period === 'quarterly'
            ? (rangeLimit - 1) * 3
            : (rangeLimit - 1) * 12;
    const startValue = nowValue - span;

    const isInRange = (year, monthIndex) => {
        const value = toMonthValue(year, monthIndex);
        return value >= startValue && value <= nowValue;
    };

    monthKeys.forEach(monthKey => {
        const match = /^(\d{4})-(\d{2})$/.exec(monthKey);
        if (!match) return;
        const year = Number(match[1]);
        const monthIndex = Number(match[2]) - 1;
        if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) return;

        if (!isInRange(year, monthIndex)) return;

        const inner = data[monthKey];
        if (!inner || typeof inner !== 'object') return;
        const total = Object.values(inner).reduce(
            (sum, v) => sum + (Number(v) || 0),
            0
        );

        if (period === 'monthly') {
            buckets.set(monthKey, (buckets.get(monthKey) || 0) + total);
        } else if (period === 'quarterly') {
            const q = Math.floor(monthIndex / 3) + 1;
            const key = `${year}-Q${q}`;
            buckets.set(key, (buckets.get(key) || 0) + total);
        } else {
            const key = String(year);
            buckets.set(key, (buckets.get(key) || 0) + total);
        }
    });

    const labels = Array.from(buckets.keys()).sort();
    const values = labels.map(label => buckets.get(label) || 0);

    return {
        labels,
        datasets: [
            {
                label: row?.code || '',
                data: values,
            },
        ],
    };
};

const buildChartsMap = (context, sections, analyticsMap, currentDevice) => {
    const map = {};
    if (!Array.isArray(sections)) return map;

    const resolvedTimeZone = resolveTimeZone(
        context?.timeZone || context?.time_zone
    );
    const chartItems = [];

    sections.forEach(section => {
        if (section && section.type === 'charts-row') {
            (section.items || []).forEach(item => {
                if (item && item.chartKey) {
                    chartItems.push(item);
                }
            });
        }
    });

    chartItems.forEach(item => {
        const code = item.chartKey;
        const itemDevice = item.device || 'Total';

        const handleCombinedTopFive = (variantItems, labelResolver) => {
            const variantCharts = variantItems
                .map(variant => {
                    if (!variant || !variant.code) return null;
                    const variantCode = variant.code;
                    const analyticsKey = `${variantCode}__${itemDevice}`;
                    const row = analyticsMap && analyticsMap[analyticsKey];
                    if (!row) return null;

                    const chartOverride = CHART_OVERRIDES[variantCode] || {};

                    if (
                        !isChartVisibleForDevice(chartOverride, currentDevice)
                    ) {
                        return null;
                    }

                    const chartConfig = buildChartConfigFromAnalyticsRow(row, {
                        timeZone: resolvedTimeZone,
                    });
                    if (!chartConfig) return null;

                    // Monthly variant: replace data with current-month only
                    if (variantCode.endsWith('_past_monthly')) {
                        chartConfig.data = aggregateTopFiveData(
                            row,
                            'current-month',
                            resolvedTimeZone
                        );
                    }

                    chartConfig.title =
                        typeof chartOverride.titleResolver === 'function'
                            ? chartOverride.titleResolver(row, chartConfig)
                            : extractCardTitle(row, chartConfig);
                    chartConfig.subTitle =
                        typeof chartOverride.infoResolver === 'function'
                            ? chartOverride.infoResolver(row, chartConfig)
                            : extractCardInfo(row, chartConfig);
                    chartConfig.info = extractCardInfo(row, chartConfig);

                    const enriched = [
                        {
                            code: variantCode,
                            label: variant.label || labelResolver(variantCode),
                            chart: chartConfig,
                        },
                    ];

                    // If monthly variant, add derived quarterly/yearly buckets
                    if (variantCode.endsWith('_past_monthly')) {
                        const quarterlyData = aggregateTopFiveData(
                            row,
                            'quarterly',
                            resolvedTimeZone
                        );
                        const yearlyData = aggregateTopFiveData(
                            row,
                            'yearly',
                            resolvedTimeZone
                        );

                        const baseMeta = {
                            type: chartConfig.type,
                            options: chartConfig.options,
                            title: chartConfig.title,
                            subTitle: chartConfig.subTitle,
                            info: chartConfig.info,
                        };

                        enriched.push(
                            {
                                code: `${variantCode}__quarterly`,
                                label: 'chart.chart_selector__period_quarterly',
                                chart: {
                                    ...baseMeta,
                                    data: quarterlyData,
                                },
                            },
                            {
                                code: `${variantCode}__yearly`,
                                label: 'chart.chart_selector__period_yearly',
                                chart: {
                                    ...baseMeta,
                                    data: yearlyData,
                                },
                            }
                        );
                    }

                    return enriched;
                })
                .filter(Boolean)
                .flat();

            if (!variantCharts.length) return null;

            const baseChart = variantCharts[0].chart;

            map[code] = {
                ...baseChart,
                variants: variantCharts,
                selectedCode: variantCharts[0].code,
            };
            return map[code];
        };

        const handleCombinedVariants = (variantItems, labelResolver) => {
            const variantCharts = variantItems
                .map(variant => {
                    if (!variant || !variant.code) return null;
                    const variantCode = variant.code;
                    const analyticsKey = `${variantCode}__${itemDevice}`;
                    const row = analyticsMap && analyticsMap[analyticsKey];
                    if (!row) return null;

                    const chartOverride = CHART_OVERRIDES[variantCode] || {};
                    if (
                        !isChartVisibleForDevice(chartOverride, currentDevice)
                    ) {
                        return null;
                    }

                    const buildVersionWindowVariants = () => {
                        const baseOptions =
                            typeof chartOverride.optionsBuilder === 'function'
                                ? chartOverride.optionsBuilder(row)
                                : {};

                        const buildChart = win => {
                            const data = formatVersionWindowDistribution(
                                row,
                                win
                            );
                            return {
                                id: `${variantCode}__${win}`,
                                type: chartOverride.chartType || 'bar',
                                title:
                                    typeof chartOverride.titleResolver ===
                                    'function'
                                        ? chartOverride.titleResolver(row, {})
                                        : extractCardTitle(row, {}),
                                subTitle:
                                    typeof chartOverride.infoResolver ===
                                    'function'
                                        ? chartOverride.infoResolver(row, {})
                                        : extractCardInfo(row, {}),
                                info: extractCardInfo(row, {}),
                                data,
                                options: {
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    ...baseOptions,
                                },
                            };
                        };

                        return [
                            {
                                code: `${variantCode}__30d`,
                                label: 'chart.chart_selector__period_last_30_days',
                                chart: buildChart('30d'),
                            },
                            {
                                code: `${variantCode}__quarter`,
                                label: 'chart.chart_selector__period_last_quarter',
                                chart: buildChart('quarter'),
                            },
                            {
                                code: `${variantCode}__year`,
                                label: 'chart.chart_selector__period_last_year',
                                chart: buildChart('year'),
                            },
                        ];
                    };

                    if (
                        variantCode.endsWith('_version_past_monthly') ||
                        variantCode.endsWith('_version_monthly')
                    ) {
                        return buildVersionWindowVariants();
                    }

                    const chartConfig = buildChartConfigFromAnalyticsRow(row, {
                        timeZone: resolvedTimeZone,
                    });
                    if (!chartConfig) return null;

                    chartConfig.title =
                        typeof chartOverride.titleResolver === 'function'
                            ? chartOverride.titleResolver(row, chartConfig)
                            : extractCardTitle(row, chartConfig);
                    chartConfig.subTitle =
                        typeof chartOverride.infoResolver === 'function'
                            ? chartOverride.infoResolver(row, chartConfig)
                            : extractCardInfo(row, chartConfig);
                    chartConfig.info = extractCardInfo(row, chartConfig);

                    return {
                        code: variantCode,
                        label: variant.label || labelResolver(variantCode),
                        chart: chartConfig,
                    };
                })
                .filter(Boolean)
                .flat();

            if (!variantCharts.length) return null;

            const baseChart = variantCharts[0].chart;

            map[code] = {
                ...baseChart,
                variants: variantCharts,
                selectedCode: variantCharts[0].code,
            };
            return map[code];
        };

        if (code === TEST_RESULT_MONTHLY_COMBINED_KEY) {
            const baseCode = item.baseCode || TEST_RESULT_MONTHLY_CODE;
            const analyticsKey = `${baseCode}__${itemDevice}`;
            const row = analyticsMap && analyticsMap[analyticsKey];
            if (!row) return;

            const chartOverride = CHART_OVERRIDES[baseCode] || {};
            const baseOptions =
                typeof chartOverride.optionsBuilder === 'function'
                    ? chartOverride.optionsBuilder(row)
                    : {};

            const modeDefs = Array.isArray(item.modes)
                ? item.modes
                : TEST_RESULT_MODES;
            const chartsByKey = {};
            let firstChart = null;

            modeDefs.forEach(modeDef => {
                const ranges =
                    Array.isArray(modeDef.ranges) && modeDef.ranges.length
                        ? modeDef.ranges
                        : [null]; // yearly uses null = all

                ranges.forEach(r => {
                    const timeline = formatTestResultTimeline(
                        row,
                        modeDef.code,
                        r
                    );
                    if (!timeline.labels || !timeline.labels.length) {
                        return;
                    }
                    const { labels, datasets, percentages } = timeline;

                    const chartKey = `${modeDef.code}-${
                        r === null ? 'all' : r
                    }`;
                    chartsByKey[chartKey] = {
                        id: `${baseCode}-${chartKey}`,
                        type: 'bar',
                        title:
                            typeof chartOverride.titleResolver === 'function'
                                ? chartOverride.titleResolver(row, {})
                                : extractCardTitle(row, {}),
                        subTitle:
                            typeof chartOverride.infoResolver === 'function'
                                ? chartOverride.infoResolver(row, {})
                                : extractCardInfo(row, {}),
                        info: extractCardInfo(row, {}),
                        data: { labels, datasets },
                        percentages,
                        options: {
                            responsive: true,
                            maintainAspectRatio: false,
                            ...baseOptions,
                            plugins: {
                                ...(baseOptions.plugins || {}),
                                legend: {
                                    display: true,
                                    position: 'bottom',
                                    ...(baseOptions.plugins &&
                                    baseOptions.plugins.legend
                                        ? baseOptions.plugins.legend
                                        : {}),
                                },
                                datalabels: {
                                    color: '#333',
                                    formatter: (value, ctx) => {
                                        const dataIndex = ctx.dataIndex ?? 0;
                                        const datasets =
                                            ctx.chart?.data?.datasets || [];
                                        const total = datasets.reduce(
                                            (sum, ds) =>
                                                sum +
                                                (Number(
                                                    Array.isArray(ds.data)
                                                        ? ds.data[dataIndex]
                                                        : 0
                                                ) || 0),
                                            0
                                        );
                                        const pct =
                                            total > 0
                                                ? ` ${(
                                                      (value / total) *
                                                      100
                                                  ).toFixed(1)}%`
                                                : ' 0%';
                                        return `${pct}`;
                                    },
                                    ...(baseOptions.plugins &&
                                    baseOptions.plugins.datalabels
                                        ? baseOptions.plugins.datalabels
                                        : {}),
                                },
                                tooltip: {
                                    callbacks: {
                                        label: context => {
                                            const label =
                                                context.dataset?.label || '';
                                            const value =
                                                context.parsed?.y ??
                                                context.parsed ??
                                                0;
                                            const dataIndex =
                                                context.dataIndex ?? 0;
                                            const datasets =
                                                context.chart?.data?.datasets ||
                                                [];
                                            const total = datasets.reduce(
                                                (sum, ds) =>
                                                    sum +
                                                    (Number(
                                                        Array.isArray(ds.data)
                                                            ? ds.data[dataIndex]
                                                            : 0
                                                    ) || 0),
                                                0
                                            );
                                            const pct =
                                                total > 0
                                                    ? ` (${(
                                                          (value / total) *
                                                          100
                                                      ).toFixed(1)}%)`
                                                    : '';
                                            const base =
                                                label && label.length
                                                    ? `${label}: ${value}`
                                                    : `${value}`;
                                            return `${base}${pct}`;
                                        },
                                    },
                                    ...(baseOptions.plugins &&
                                    baseOptions.plugins.tooltip
                                        ? baseOptions.plugins.tooltip
                                        : {}),
                                },
                            },
                            scales: {
                                x: {
                                    stacked: true,
                                    ...(baseOptions.scales &&
                                    baseOptions.scales.x
                                        ? baseOptions.scales.x
                                        : {}),
                                },
                                y: {
                                    stacked: true,
                                    beginAtZero: true,
                                    ...(baseOptions.scales &&
                                    baseOptions.scales.y
                                        ? baseOptions.scales.y
                                        : {}),
                                },
                            },
                        },
                    };
                    if (!firstChart) {
                        firstChart = chartsByKey[chartKey];
                    }
                });
            });

            if (!firstChart) return;

            map[code] = {
                ...firstChart,
                chartsByKey,
                modeOptions: modeDefs,
                rangeOptionsByMode: modeDefs.reduce((acc, def) => {
                    acc[def.code] = def.ranges || [];
                    return acc;
                }, {}),
                selectedMode: modeDefs[0]?.code || 'monthly',
                selectedRange: modeDefs[0]?.ranges?.[0] || null,
                baseCode,
            };
            return;
        }

        if (COMBINED_GROUP_DEF_MAP.has(code)) {
            const def = COMBINED_GROUP_DEF_MAP.get(code);
            const variants = Array.isArray(item.variants) ? item.variants : [];
            const labelResolver =
                typeof def.labelResolver === 'function'
                    ? def.labelResolver
                    : v => v;
            const handlerType = def.handler || 'variant';
            const res =
                handlerType === 'topFive'
                    ? handleCombinedTopFive(variants, labelResolver)
                    : handleCombinedVariants(variants, labelResolver);
            if (res) return;
        }

        const analyticsKey = `${code}__${itemDevice}`;
        const row = analyticsMap && analyticsMap[analyticsKey];
        if (!row) return;

        const chartConfig = buildChartConfigFromAnalyticsRow(row, {
            timeZone: resolvedTimeZone,
        });
        if (chartConfig) {
            // Final title/subTitle resolution (allow override functions)
            const chartOverride = CHART_OVERRIDES[code] || {};

            // Check visibility based on current device
            if (!isChartVisibleForDevice(chartOverride, currentDevice)) {
                return;
            }

            chartConfig.title =
                typeof chartOverride.titleResolver === 'function'
                    ? chartOverride.titleResolver(row, chartConfig)
                    : extractCardTitle(row, chartConfig);
            chartConfig.subTitle =
                typeof chartOverride.infoResolver === 'function'
                    ? chartOverride.infoResolver(row, chartConfig)
                    : extractCardInfo(row, chartConfig);
            chartConfig.info = extractCardInfo(row, chartConfig);
            map[code] = chartConfig;
        }
    });
    return map;
};

const filterLayoutByDevice = (layout, device) => {
    if (!Array.isArray(layout)) return [];
    if (!device) return layout.slice();
    return layout.filter(item => {
        const itemDevice =
            item && item.chart_device ? item.chart_device : 'Total';
        return itemDevice === device;
    });
};

const extractDevicesFromLayout = layout => {
    const seen = new Set();
    const devices = [];

    const pushDevice = device => {
        const dev = device || 'Total';
        if (seen.has(dev)) return;
        seen.add(dev);
        devices.push(dev);
    };

    // 確保 Total（All models）優先出現
    pushDevice('Total');

    if (Array.isArray(layout)) {
        layout.forEach(item => {
            if (!item || !item.chart_device) return;
            pushDevice(item.chart_device);
        });
    }

    return devices;
};

/*
 * 建立圖表資料抓取計畫，拿到group chart回應後，根據版面設定決定要抓哪些圖表資料
 */
const buildChartFetchPlan = groupLayout => {
    if (!Array.isArray(groupLayout) || groupLayout.length === 0) {
        return [];
    }

    const unique = new Map();
    groupLayout.forEach(item => {
        if (!item || !item.chart_code) return;
        const code = item.chart_code;
        const device = item.chart_device || 'Total';
        const key = `${code}__${device}`;
        if (!unique.has(key)) {
            unique.set(key, { code, device });
        }
    });

    return Array.from(unique.values());
};

const fetchAnalyticsForLayout = async (
    chartFetchPlan,
    context,
    callbacks = {}
) => {
    const {
        onChartFetchStart,
        onChartFetchEnd,
        onChartRow,
        maxConcurrency = 6,
    } = callbacks;
    const map = {};
    if (!Array.isArray(chartFetchPlan) || chartFetchPlan.length === 0) {
        return map;
    }

    const { level = 'global', levelId = null } = context || {};

    let cursor = 0;
    const takeNext = () => {
        if (cursor >= chartFetchPlan.length) return null;
        const next = chartFetchPlan[cursor];
        cursor += 1;
        return next;
    };

    const worker = async () => {
        for (;;) {
            const next = takeNext();
            if (!next) break;
            const { code, device } = next;

            if (typeof onChartFetchStart === 'function') {
                onChartFetchStart(code, device);
            }
            try {
                const response = await apiClient.get('/analytics/chart', {
                    params: {
                        code,
                        device,
                        level,
                        level_id: levelId,
                    },
                });

                const payload = response.data || {};
                const results = Array.isArray(payload.results)
                    ? payload.results
                    : [];
                if (!results.length) {
                    continue;
                }

                const row = results[0];
                const analyticsKey = `${code}__${device}`;
                map[analyticsKey] = row;

                if (typeof onChartRow === 'function') {
                    onChartRow(row, { code, device, analyticsKey });
                }
            } catch (error) {
                console.error(
                    'Failed to load analytics chart',
                    code,
                    device,
                    error
                );
            } finally {
                if (typeof onChartFetchEnd === 'function') {
                    onChartFetchEnd(code, device);
                }
            }
        }
    };

    const workerCount = Math.max(
        1,
        Math.min(Number(maxConcurrency) || 1, chartFetchPlan.length)
    );
    const tasks = Array.from({ length: workerCount }, () => worker());

    await Promise.all(tasks);

    return map;
};

// 主流程：取得進階儀表板設定group_chart 與資料
export const fetchAdvancedDashboardConfigFromAPI = async (
    context,
    callbacks = {}
) => {
    const {
        onLayoutFetchStart,
        onLayoutFetchEnd,
        onChartFetchPlan,
        onChartFetchStart,
        onChartFetchEnd,
        onLayoutReady,
        onChartData,
    } = callbacks || {};
    // context: { role, topTab, model, level, levelId, device }
    const resolvedTimeZone = resolveTimeZone(
        (context && (context.timeZone || context.time_zone)) || undefined
    );
    const safeContext = {
        ...(context || {}),
        timeZone: resolvedTimeZone,
    };
    const { device } = safeContext;

    let groupLayout = [];
    if (typeof onLayoutFetchStart === 'function') {
        onLayoutFetchStart();
    }
    try {
        const response = await apiClient.get('/analytics/group_chart');
        if (response && Array.isArray(response.data)) {
            groupLayout = response.data;
        }
    } catch (error) {
        // 若 group_chart 失敗，fallback 為空陣列，前端會使用完整預設版面
        console.error('Failed to load analytics group_chart layout', error);
        groupLayout = [];
    } finally {
        if (typeof onLayoutFetchEnd === 'function') {
            onLayoutFetchEnd(groupLayout);
        }
    }

    const availableDevices = extractDevicesFromLayout(groupLayout);
    const filteredLayout = filterLayoutByDevice(groupLayout, device);
    const chartFetchPlan = buildChartFetchPlan(filteredLayout);
    if (typeof onChartFetchPlan === 'function') {
        onChartFetchPlan(chartFetchPlan);
    }

    const sections = buildSections(safeContext, filteredLayout);

    if (typeof onLayoutReady === 'function') {
        onLayoutReady({
            sections,
            availableDevices,
            chartFetchPlan,
        });
    }

    const analyticsMap = {};
    const handleChartRow = (row, meta) => {
        const key = meta.analyticsKey || `${meta.code}__${meta.device}`;
        analyticsMap[key] = row;
        if (typeof onChartData === 'function') {
            const metricsPartial = buildMetrics(
                filteredLayout,
                analyticsMap,
                device,
                resolvedTimeZone
            );
            const chartsPartial = buildChartsMap(
                safeContext,
                sections,
                analyticsMap,
                device
            );
            const latestPartial = findLatestCalculateDate(analyticsMap);

            onChartData({
                metrics: metricsPartial,
                charts: chartsPartial,
                latestCalculateDate: latestPartial,
            });
        }
    };

    await fetchAnalyticsForLayout(chartFetchPlan, safeContext, {
        onChartFetchStart,
        onChartFetchEnd,
        onChartRow: handleChartRow,
    });

    const metrics = buildMetrics(
        filteredLayout,
        analyticsMap,
        device,
        resolvedTimeZone
    );
    const charts = buildChartsMap(safeContext, sections, analyticsMap, device);
    const latestCalculateDate = findLatestCalculateDate(analyticsMap);

    return {
        sections,
        metrics,
        charts,
        latestCalculateDate,
        availableDevices,
        chartFetchPlan,
    };
};
